import { useMemo, useState } from "react";
import { ChevronRight, Folder, Loader2, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router";
import {
  PROJECT_STATUS_LABELS,
  countryLabel,
  progressToHandover,
  type Project,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { DataTable, type DataTableColumn } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { useProjects } from "@/features/project/api";
import { cn } from "@/lib/utils";
import { DISPLAY_LOCALE } from "@/lib/format";

/**
 * The workspace's front door: every site, pick one to open.
 *
 * `DataTable` with `onSelect` rather than a grid of cards. Three reasons, in
 * order of weight:
 *
 *  1. `onSelect` already renders each row as a real `<button>`, so focus order
 *     and keyboard activation come for free. This is the app's primary
 *     navigation control; a clickable card grid means either wrapping each card
 *     in a button (which fights the card's own layout) or hand-rolling
 *     `role="button"`/`tabIndex`/`onKeyDown` and getting some part of it subtly
 *     wrong.
 *  2. `DataTable` was the one primitive in `components/gb/` with no production
 *     use at all — only the style guide. "Reuse what exists" applies most
 *     strongly to the thing that exists and is going unused.
 *  3. A picker is a list of things to open, which is the shape the component
 *     was built from in the first place.
 *
 * Archive and unarchive deliberately live on the project's own overview, not
 * here: `DataTable` makes the whole row a button, and a button nested inside a
 * button is invalid HTML.
 */
export function ProjectPicker() {
  const navigate = useNavigate();
  const { data: projects, isPending, isError, error, refetch } = useProjects();
  const [showArchived, setShowArchived] = useState(false);

  const archivedCount =
    projects?.filter((p) => p.status === "archived").length ?? 0;
  const visible = useMemo(
    () =>
      (projects ?? []).filter((p) => showArchived || p.status !== "archived"),
    [projects, showArchived],
  );

  if (isPending) {
    return (
      <ContentColumn>
        <div
          className="flex items-center gap-2 py-16 text-muted"
          role="status"
          aria-live="polite"
        >
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Loading projects…</span>
        </div>
      </ContentColumn>
    );
  }

  if (isError) {
    return (
      <ContentColumn>
        <div className="rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">
            Couldn’t load projects.
          </p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">
            {error instanceof Error ? error.message : "Something went wrong."}
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-4"
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </div>
      </ContentColumn>
    );
  }

  if (projects.length === 0) {
    return (
      <ContentColumn>
        <EmptyState
          icon={Folder}
          title="No projects yet"
          description="A project is a site: its crew, its documents, its daily record and its Brain."
          action={
            <Button asChild>
              <Link to="/projects/new">
                <Plus size={16} />
                Create your first project
              </Link>
            </Button>
          }
        />
      </ContentColumn>
    );
  }

  return (
    <ContentColumn>
      <div className="mb-4 flex items-end justify-between gap-4">
        <SectionLabel trailing={`${visible.length} shown`}>
          Projects
        </SectionLabel>
        <div className="flex items-center gap-3">
          {archivedCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowArchived((v) => !v)}
              aria-pressed={showArchived}
            >
              {showArchived
                ? "Hide archived"
                : `Show archived (${archivedCount})`}
            </Button>
          )}
          <Button asChild size="sm">
            <Link to="/projects/new">
              <Plus size={16} />
              New project
            </Link>
          </Button>
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        rows={visible}
        getRowKey={(p) => p.id}
        onSelect={(p) => void navigate(`/projects/${p.id}`)}
        emptyMessage="No active projects. Show archived to see the rest."
      />
    </ContentColumn>
  );
}

const STATUS_STYLES: Record<Project["status"], string> = {
  active: "border-ok/40 bg-ok/10 text-ok",
  "setup-incomplete": "border-warn/40 bg-warn/10 text-warn",
  archived: "border-border bg-canvas text-muted",
};

const STATUS_LABELS = PROJECT_STATUS_LABELS;

/** Today as `YYYY-MM-DD`, which is what `progressToHandover` compares against. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** `15 Feb 2026`, or an em dash when the date was never given. */
function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

const COLUMNS: ReadonlyArray<DataTableColumn<Project>> = [
  {
    key: "status",
    header: "Status",
    width: 110,
    render: (p) => (
      <span
        className={cn(
          "inline-flex items-center rounded-full border px-2.5 py-[3px] font-display text-[0.68rem] font-semibold tracking-[0.06em] uppercase",
          STATUS_STYLES[p.status],
        )}
      >
        {STATUS_LABELS[p.status]}
      </span>
    ),
  },
  {
    key: "name",
    header: "Project",
    render: (p) => (
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink">{p.name}</div>
        <div className="truncate text-[0.82rem] text-muted">
          {[p.region, p.spec].filter(Boolean).join(" · ") || "No details yet"}
        </div>
      </div>
    ),
  },
  {
    key: "country",
    header: "Country",
    width: 92,
    render: (p) => (
      <span className="text-[0.85rem] text-ink-slate">
        {countryLabel(p.country)}
      </span>
    ),
  },
  {
    key: "window",
    header: "Programme",
    width: 190,
    render: (p) => (
      <span className="text-[0.82rem] text-ink-slate">
        {formatDate(p.startDate)} – {formatDate(p.targetHandoverDate)}
      </span>
    ),
  },
  {
    key: "progress",
    header: "Progress",
    width: 120,
    render: (p) => {
      // Null whenever either date is missing, which is normal mid-setup —
      // showing "0%" there would read as "nothing has happened" rather than
      // "we have not been told the dates".
      const pct = progressToHandover(
        p.startDate,
        p.targetHandoverDate,
        today(),
      );
      if (pct === null)
        return <span className="text-[0.82rem] text-muted">—</span>;
      return (
        <div className="flex items-center gap-2">
          <div className="h-[6px] flex-1 overflow-hidden rounded-full bg-canvas">
            <div
              className="h-full rounded-full bg-brand"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="shrink-0 text-[0.75rem] font-semibold text-ink-slate">
            {pct}%
          </span>
        </div>
      );
    },
  },
  {
    key: "chevron",
    width: 18,
    render: () => <ChevronRight size={18} className="text-muted" />,
  },
];
